import React, { useEffect, useState, useCallback } from 'react';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { useUndoStore } from '../../store/undoStore';

const UndoToast: React.FC = () => {
  const { lastAction, isUndoing, undo } = useUndoStore();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  // Raccourci clavier global Ctrl+Z / Cmd+Z
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      // Ne pas intercepter si on est dans un input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;

      if (lastAction) {
        e.preventDefault();
        undo();
      }
    }
  }, [lastAction, undo]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Animation d'apparition / disparition
  useEffect(() => {
    if (lastAction) {
      setVisible(true);
      setProgress(100);

      // Barre de progression
      const duration = 10000;
      const interval = 50;
      const step = (100 * interval) / duration;
      const timer = setInterval(() => {
        setProgress(prev => {
          if (prev <= 0) {
            clearInterval(timer);
            return 0;
          }
          return prev - step;
        });
      }, interval);

      return () => clearInterval(timer);
    } else {
      setVisible(false);
    }
  }, [lastAction?.id]);

  if (!visible && !lastAction) return null;

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[99998] transition-all duration-300 ${
        visible && lastAction ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div className="bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 rounded-xl shadow-lg overflow-hidden min-w-[280px] max-w-[400px]">
        <div className="flex items-center gap-3 px-4 py-3">
          <ArrowUturnLeftIcon className="w-4 h-4 flex-shrink-0 opacity-60" />
          <span className="text-sm font-medium flex-1 truncate">
            {isUndoing ? 'Annulation...' : lastAction?.label}
          </span>
          <button
            onClick={undo}
            disabled={isUndoing}
            className="text-primary-400 dark:text-primary-600 text-sm font-semibold hover:text-primary-300 dark:hover:text-primary-700 transition-colors flex-shrink-0"
          >
            {isUndoing ? '...' : 'Annuler'}
          </button>
          <kbd className="hidden sm:inline text-xs opacity-40 bg-white/10 dark:bg-black/10 px-1.5 py-0.5 rounded font-mono">
            ⌘Z
          </kbd>
        </div>
        {/* Barre de progression */}
        <div className="h-0.5 bg-white/10 dark:bg-black/10">
          <div
            className="h-full bg-primary-500 transition-none"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default UndoToast;
